import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import IconButton from "../../../../../components/base/button/icon_button";
import {Add, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import Button from "../../../../../components/base/button/button";
import TryAgain from "../../../../../components/app-specific/try-again";
import UpsertProductRelationTypeDialog from "../../../../../components/dialogs/upsert-product-relation-type";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {ProductRelationTypesDataGridColumns} from "../../../../../../core/constants/table/data-grid-column-keys";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {Box, CircularProgress} from "@mui/material";
import {ReactComponent as Passed} from "../../../../../../assets/images/validations/passed.svg";
import {ReactComponent as Failed} from "../../../../../../assets/images/validations/failed.svg";

const dataGridSaveConfig = {
    name: 'ProductRelationTypesViewDataGrid',
    version: 2,
};

const initialUpsertDialogState = {open: false, relationType: null};

const BooleanIndicator = ({enabled}) => (
    enabled ? <Passed/> : <Failed/>
);

const ProductRelationTypesView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.products-relation-types'});
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [upsertDialogState, setUpsertDialogState] = useState(initialUpsertDialogState);
    const [removingIds, setRemovingIds] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('products-relation-types-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * Configures the notebook tags and behavioral analytics tool for this view.
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsRelationTypes);
        configureBehavioralTool((tags) => tags.productsRelationTypes);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches all product relation types from the API and updates the state.
     * @type {(function(): Promise<void>)|*}
     */
    const getProductRelationTypes = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllPartRelationTypes();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTypes(response?.data ?? []);
        }
        setLoading(false);
    }, [isMounted]);

    /**
     * Fetches the product relation types when the component mounts.
     */
    useEffect(() => {
        getProductRelationTypes().then();
    }, [getProductRelationTypes]);

    /**
     * Handles the upsert (create/update) of a product relation type. If the relation type already exists in the state, it updates it; otherwise, it adds it to the list.
     * @type {(function(*): void)|*}
     */
    const onUpsertProductRelationType = useCallback((val) => {
        setTypes(prevState => {
            const existingIndex = prevState.findIndex(e => e.id === val.id);
            if (existingIndex !== -1) {
                const newState = [...prevState];
                newState[existingIndex] = {...newState[existingIndex], ...val};
                return newState;
            }
            return [...prevState, val];
        });
    }, []);

    /**
     * Removes a product relation type by calling the API and updating the state accordingly. It also manages a list of removing IDs to disable the remove button and show a loading state for the specific item being removed.
     * @type {(function(*): Promise<void>)|*}
     */
    const removeRelationType = useCallback(async (relationType) => {
        setRemovingIds(prevState => [...prevState, relationType.id]);
        const response = await Api.deletePartRelationType(relationType.id);
        if (!isMounted())
            return;
        setRemovingIds(prevState => prevState.filter(id => id !== relationType.id));
        if (response?.resultFlag) {
            setTypes(prevState => prevState.filter(e => e.id !== relationType.id));
        }
    }, [isMounted]);

    /**
     * Opens a confirmation dialog before removing a product relation type. If the user confirms, it calls the removeRelationType function.
     * @type {(function(*): void)|*}
     */
    const getRemoveConfirmation = useCallback((relationType) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {t('removeConfirmation')}
                </p>
            </div>,
            {maxWidth: 'sm'},
            (v) => v && removeRelationType(relationType),
        );
    }, [removeRelationType, t]);

    /**
     * Opens the upsert dialog for a given product relation type. If no relation type is provided, it opens the dialog for creating a new one.
     * @type {(function(*): void)|*}
     */
    const openUpsertDialog = useCallback((relationType) => {
        setUpsertDialogState({
            open: true,
            relationType: relationType ?? null,
        });
    }, []);

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            {
                name: ProductRelationTypesDataGridColumns.actions,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                resizable: false,
                width: 40,
            },
            {
                title: t('columns.name'),
                name: ProductRelationTypesDataGridColumns.title,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 250,
                sortable: false,
            },
            {
                title: t('columns.connectedParts'),
                name: ProductRelationTypesDataGridColumns.connectedParts,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: t('columns.suggestiveSelling'),
                name: ProductRelationTypesDataGridColumns.suggestiveSelling,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 120,
            },
            // {
            //     title: t('columns.automaticallyAddedToCart'),
            //     name: ProductRelationTypesDataGridColumns.automaticallyAddedToCart,
            //     alignment: DataGridColumnAlignments.center,
            //     type: DataGridColumnTypes.element,
            //     sortable: false,
            //     width: 120,
            // },
        ], [t]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });

    const dataGridRows = useMemo(() => {
        return types?.map(relationType => {
            const removing = removingIds.includes(relationType.id);
            return ({
                key: relationType.id,
                data: relationType,
                cells: {
                    [ProductRelationTypesDataGridColumns.actions]: relationType.isReadOnly
                        ? ' '
                        : (
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                <IconButton
                                    onClick={() => getRemoveConfirmation(relationType)}
                                    disabled={removing}
                                    color={'error'}
                                    size={'small'}
                                    sx={{backgroundColor: 'background.paper'}}
                                >
                                    {
                                        removing
                                            ? <CircularProgress size={15} color={'primary'}/>
                                            : <DeleteOutlined/>
                                    }
                                </IconButton>
                                <IconButton
                                    disabled={removing}
                                    size={'small'}
                                    onClick={() => openUpsertDialog(relationType)}
                                    color={'primary'}
                                    sx={{backgroundColor: 'background.paper'}}
                                >
                                    <EditOutlined/>
                                </IconButton>
                            </Box>
                        ),
                    [ProductRelationTypesDataGridColumns.title]: relationType?.title ?? '',
                    [ProductRelationTypesDataGridColumns.connectedParts]: relationType.relatedParts ?? 0,
                    [ProductRelationTypesDataGridColumns.suggestiveSelling]:
                        <BooleanIndicator enabled={relationType.functionality === 'Cross-Sale'}/>,
                    [ProductRelationTypesDataGridColumns.automaticallyAddedToCart]:
                        <BooleanIndicator enabled={relationType.automaticallyAddedToCart}/>,
                },
            });
        });
    }, [getRemoveConfirmation, openUpsertDialog, removingIds, types]);

    return (
        <>
            <div className={'d-flex justify-content-between w-100 my-4'}>
                <p className={'view-title'}>
                    {t('pageTitle')}
                </p>
                <Button
                    className='d-flex align-items-center justify-content-center'
                    variant={'outlined'}
                    color={'primary'}
                    onClick={() => openUpsertDialog()}
                >
                    <Add className={'mr-2'}/>
                    <p>{t('addButton')}</p>
                </Button>
            </div>

            <div className={'part-info-paper'}>
                {
                    !loading && types?.length < 1
                        ? <TryAgain text={t('emptyState')}/>
                        : <PartnerDataGrid
                            {...dataGridSaveConfig}
                            initialLayoutState={{columns: columns}}
                            rows={dataGridRows}
                            columns={dataGridColumns}
                            hideExporting
                            hideFooterActions
                            hideFooter
                            state={{
                                loading: {state: loading},
                                density: dataGridDensity,
                            }}
                        />
                }
            </div>
            <UpsertProductRelationTypeDialog
                open={upsertDialogState.open}
                setOpen={() => setUpsertDialogState(initialUpsertDialogState)}
                relationType={upsertDialogState.relationType}
                onSuccess={onUpsertProductRelationType}
            />
        </>
    );
};

export default ProductRelationTypesView;
